import { useMemo } from 'preact/hooks';
import { ZoomViewerPosition, ZoomType, DisplayMode } from '../../typing/enums';
import { getValueFromRange } from '../../utils/number';

export const useZoomUtils = (props, state, wrapper) => {
  return useMemo(() => {
    const getWrapper = () => {
      if (!wrapper.current?.base) throw new Error('Zoom wrapper is undefined');

      return wrapper.current.base;
    };

    const isInline = () => {
      return ZoomType.INLINE === props.type && !props.viewerContainer;
    };

    const isPopup = () => {
      return !props.inPopup && ZoomType.POPUP === props.type && !props.viewerContainer;
    };

    const isMultiZoom = () => {
      return props.inPopup && props.context.config.selectZoomPropsSteps() > 0;
    };

    const getCurrentAsset = () => {
      const mediaAssets = props.context.config.selectMediaAssets();
      const currentAsset =
        props.asset ||
        mediaAssets[props.index] ||
        mediaAssets.find(asset => asset.publicId === props.publicId);

      if (currentAsset.assets) {
        currentAsset.publicId = currentAsset.assets[0].publicId;
      }

      return currentAsset;
    };

    // Convert `relative to body` to `relative to wrapper` coordinates
    const getRelativeCoords = (x, y) => {
      const rect = getWrapper().getBoundingClientRect();

      return { x: x - rect.left, y: y - rect.top };
    };

    // Returns the coordinates of the pointer relative to zoom-wrapper center, with lens padding
    const getZoomPointerCoordinates = (ix, iy) => {
      const offsetShadow = props.context.config.selectZoomPropsLensShadow() ? 1 : 0;
      const lensCoords = getLensCoords();

      let { x, y } = getRelativeCoords(ix, iy);

      x = getValueFromRange(
        x,
        lensCoords.width / 2,
        props.width - lensCoords.width / 2 - offsetShadow
      );
      y = getValueFromRange(
        y,
        lensCoords.height / 2,
        props.height - lensCoords.height / 2 - offsetShadow
      );

      x = Math.round(x - props.width / 2);
      y = Math.round(y - props.height / 2);

      return { x, y };
    };

    const getLensCoords = () => {
      const currentZoomStep = Math.max(1, state.zoomStep);
      const lensWidth = props.width / (props.level * currentZoomStep);
      const lensHeight = props.height / (props.level * currentZoomStep);

      return { width: lensWidth, height: lensHeight };
    };

    // Calculate the position of the zoom wrapper
    // i.e. for positioning the flyout zoom wrapper
    const getWrapperCoords = () => {
      if (!state.isZoomed && !state.transition) {
        // Disabled zoom
        if (isInline() || isMultiZoom()) {
          return { x: 0, y: 0 };
        }

        return { x: -1000000, y: -1000000 };
      }

      if (getWrapper() && !props.viewerContainer) {
        const { height, width, context } = props;
        const position = props.viewerPosition;
        const viewerOffset = context.config.selectZoomPropsViewerOffset();
        const rect = getWrapper().getBoundingClientRect();
        const scrollLeft = window.pageXOffset;
        const scrollTop = window.pageYOffset;
        const displayMode = context.config.selectDisplayPropsMode();
        const displaySpacing = context.config.selectDisplayPropsSpacing();
        const displayColumns = context.config.selectDisplayPropsColumns();
        const isMultiColumns = displayMode === DisplayMode.EXPANDED && displayColumns > 1;
        const offset = isMultiColumns ? displaySpacing : viewerOffset;

        let top;
        if (isInline() || isMultiZoom()) {
          top = 0;
        } else if (position === ZoomViewerPosition.BOTTOM) {
          top = scrollTop + rect.top + height + offset;
        } else if (position === ZoomViewerPosition.TOP) {
          top = scrollTop + rect.top - height - offset;
        } else {
          top = rect.top + scrollTop;
        }

        let left;
        if (isInline() || isMultiZoom()) {
          left = 0;
        } else if (position === ZoomViewerPosition.RIGHT) {
          left = scrollLeft + rect.right + offset;
        } else if (position === ZoomViewerPosition.LEFT) {
          left = scrollLeft + rect.left - width - offset;
        } else {
          left = rect.left + scrollLeft;
        }

        return { y: Math.round(top), x: Math.round(left) };
      }

      return { y: 0, x: 0 };
    };

    return {
      getWrapper,
      isInline,
      isPopup,
      isMultiZoom,
      getCurrentAsset,
      getRelativeCoords,
      getZoomPointerCoordinates,
      getLensCoords,
      getWrapperCoords
    };
  }, [wrapper, props, state.zoomStep, state.isZoomed, state.transition]);
};
